// VorgangPickerAuswahl.tsx — K10/D1: die gestufte, serverseitig gesuchte und
// paginierte Vorgangs-Auswahl als INLINE-Liste (kein Dropdown, kein Fenster).
//
// Sie ersetzt zwei Altlasten auf einmal: das Fluent-`Dropdown` im TerminSidecar
// und die `VorgangSuchListe`, die `getAllVorgaenge()` holte, im Browser filterte
// und dann auf 50 Zeilen schnitt — bei einem Mandanten mit vielen Vorgängen
// war das sowohl langsam als auch unvollständig. Jetzt sucht und paginiert der
// Server, und die Rangfolge (Kontakt → eigene/Team → alle) kommt von dort.
//
// Optik: `.cal-vgd-*` aus calendar.css — bewusst keine Fluent-Formularkomponenten.
//
// Der Dateiname weicht bewusst von der Komponente ab: `vorgangPickerListe.ts`
// (die reinen Helfer) liegt daneben, und zwei Dateien, die sich nur in der
// Groß-/Kleinschreibung unterscheiden, lehnt TypeScript auf einem
// case-insensitiven Dateisystem ab.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    vorgangService,
    type VorgangPickerItem,
} from "../../../services/vorgangService";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import {
    kannMehrLaden,
    leerText,
    mitAktuellUndGruppen,
    statusTon,
} from "./vorgangPickerListe";
import { pickerScope } from "./vorgangPickerScope";
import { BereichUmschalterMitAuth } from "./BereichUmschalter";
import type { ScopeKey } from "../../../utils/vorgangScope";

/** Seitengröße — dieselbe wie im Erstell-Flow. */
const SEITE = 20;

export interface VorgangPickerAuswahlProps {
    /** Ein Vorgang wurde gewählt. */
    onPick: (v: VorgangPickerItem["vorgang"]) => void;
    /** Kontakt des Kontexts — steuert NUR die Rangstufe 1, nie die Sichtbarkeit. */
    contactId?: number | null;
    disabled?: boolean;
    autoFocus?: boolean;
    /** Optional: dieser Vorgang ist bereits verknüpft und wird markiert. */
    aktuelleVorgangId?: number | null;
    /** Ohne Suchtext ausschließlich Vorgänge des Kontakts anzeigen. */
    contactOnlyWithoutSearch?: boolean;
    /**
     * T-56: Umschalter Alle/Team/Eigene über dem Suchfeld (wie auf der
     * Vorgänge-Seite). Standard aus — die übrigen Picker bleiben, wie sie sind.
     */
    mitBereich?: boolean;
}

export const VorgangPickerAuswahl: React.FC<VorgangPickerAuswahlProps> = ({
    onPick,
    contactId = null,
    disabled = false,
    autoFocus = true,
    aktuelleVorgangId = null,
    contactOnlyWithoutSearch = false,
    mitBereich = false,
}) => {
    const [query, setQuery] = useState("");
    // T-56: beim Öffnen immer „Alle" — das heutige Verhalten bleibt der Start.
    const [bereich, setBereich] = useState<ScopeKey>("alle");
    const debounced = useDebouncedValue(query, 300);
    const suchbegriff = debounced.trim();
    const [items, setItems] = useState<VorgangPickerItem[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [laden, setLaden] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const scope = pickerScope(contactOnlyWithoutSearch, suchbegriff);

    const ladeSeite = useCallback(
        async (offset: number, signal?: AbortSignal) => {
            setLaden(true);
            setFehler(null);
            try {
                const { items: neu, total: t } = await vorgangService.getPickerPage({
                    contactId,
                    q: suchbegriff || undefined,
                    scope,
                    // T-55: Der verknüpfte Vorgang steht dann ganz oben.
                    aktuelleVorgangId,
                    bereich: mitBereich ? bereich : undefined,
                    limit: SEITE,
                    offset,
                    signal,
                });
                setTotal(t);
                setItems((prev) => (offset === 0 ? neu : [...prev, ...neu]));
            } catch (err) {
                if ((err as Error)?.name === "AbortError") return;
                console.error("[VorgangPickerAuswahl] Laden fehlgeschlagen", err);
                setFehler("Vorgänge konnten nicht geladen werden.");
            } finally {
                setLaden(false);
            }
        },
        [contactId, suchbegriff, scope, aktuelleVorgangId, mitBereich, bereich],
    );

    // Erste Seite beim Mount und bei jeder Änderung von Suchbegriff/Kontakt.
    useEffect(() => {
        const ctrl = new AbortController();
        void ladeSeite(0, ctrl.signal);
        return () => ctrl.abort();
    }, [ladeSeite]);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    const zeilen = useMemo(
        () => mitAktuellUndGruppen(items, aktuelleVorgangId),
        [items, aktuelleVorgangId],
    );
    // Nie unter einer leeren Liste (wie im Mail-Picker).
    const mehr = items.length > 0 && kannMehrLaden(items.length, total);

    return (
        <>
            {mitBereich && (
                <BereichUmschalterMitAuth wert={bereich} onChange={setBereich} disabled={disabled} />
            )}
            <input
                ref={inputRef}
                type="text"
                className="cal-vgd-suche"
                placeholder="Vorgang suchen (Nummer, Titel oder Kunde)…"
                value={query}
                autoComplete="off"
                disabled={disabled}
                onChange={(e) => setQuery(e.target.value)}
            />
            {fehler && <div className="cal-vgd-fehler">{fehler}</div>}
            <div className="cal-vgd-liste">
                {laden && items.length === 0 && <div className="cal-vgd-leer">Lade Vorgänge…</div>}
                {!laden && !fehler && items.length === 0 && (
                    <div className="cal-vgd-leer">
                        {leerText({
                            mitSuche: suchbegriff.length > 0,
                            mitKontakt: contactId != null,
                            nurKontakt: scope === "contact",
                            bereich: mitBereich ? bereich : undefined,
                        })}
                    </div>
                )}
                {zeilen.map((z) => {
                    if (z.kind !== "vorgang") {
                        // T-55: Gruppenköpfe als eigene, klar abgesetzte Zeile
                        // (`vgp-gruppe`); die Optik dazu ist im Telefonie-Dialog
                        // unter `.tp-vgpick` gescopt.
                        return (
                            <div
                                key={z.kind === "gruppe" ? `g:${z.schluessel}` : "g:aktuell"}
                                className="cal-vgd-row-static vgp-gruppe"
                                role="heading"
                                aria-level={3}
                            >
                                {z.titel}
                            </div>
                        );
                    }
                    const v = z.item.vorgang;
                    const verknuepft = v.id === aktuelleVorgangId;
                    const voll = v.vorgangNbr ? `${v.vorgangNbr} · ${v.titel}` : v.titel;
                    return (
                        <button
                            key={`v:${v.id}`}
                            type="button"
                            className={`cal-vgd-row vgp-zeile${verknuepft ? " vgp-zeile--aktuell" : ""}`}
                            disabled={disabled}
                            title={voll}
                            onClick={() => onPick(v)}
                        >
                            <span className="cal-vgd-row-name vgp-kopf">
                                {v.vorgangNbr && (
                                    <>
                                        <span className="vgp-nr">{v.vorgangNbr}</span>
                                        <span className="vgp-sep"> · </span>
                                    </>
                                )}
                                <span className="vgp-titel">{v.titel}</span>
                                {verknuepft && <span className="vgp-badge"> ✓ verknüpft</span>}
                            </span>
                            <span className="cal-vgd-row-sub vgp-unter">
                                <span className="vgp-kunde">{v.kundeName ?? "ohne Kunde"}</span>
                                {v.status && (
                                    <>
                                        <span className="vgp-sep"> · </span>
                                        <span className={`vgp-status vgp-status--${statusTon(v.status)}`}>
                                            {v.status}
                                        </span>
                                    </>
                                )}
                            </span>
                        </button>
                    );
                })}
                {mehr && (
                    <button
                        type="button"
                        className="cal-vgd-row"
                        disabled={disabled || laden}
                        onClick={() => void ladeSeite(items.length)}
                    >
                        <span className="cal-vgd-row-name">
                            {laden ? "Lade…" : "Weitere Vorgänge laden"}
                        </span>
                    </button>
                )}
            </div>
        </>
    );
};

export default VorgangPickerAuswahl;
